import { emptySplitApi } from '../../app/api';
import { addFlashMessage } from '../flash-messages/flashMessagesSlice';
import {
  GetEnrichedLeadsRequest,
  GetEnrichedLeadsResponse,
  LinkEnrichedLeadRequest,
  OperationResponse,
  SyncEnrichedLeadRequest,
} from './enriched-leads-types';

function getEnrichedLeadsApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const { data } = error as { data?: { uiMessage?: string; message?: string } };
    if (data && typeof data === 'object') {
      const { uiMessage, message } = data;
      if (typeof uiMessage === 'string' && uiMessage.trim().length > 0) return uiMessage;
      if (typeof message === 'string' && message.trim().length > 0) return message;
    }
  }
  return fallback;
}

export const enrichedLeadsAPI = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({

    getEnrichedLeads: builder.query<GetEnrichedLeadsResponse, GetEnrichedLeadsRequest>({
      query: ({
        pageSize, pageNumber, searchInput, territoryId, focusCategory, linkStatus,
      }) => ({
        url: '/application-administration/enriched-leads',
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          searchInput: searchInput || undefined,
          territoryId: territoryId || undefined,
          focusCategory: focusCategory || undefined,
          linkStatus: linkStatus || undefined,
        },
      }),
      providesTags: ['EnrichedLeadsList'],
    }),

    linkEnrichedLead: builder.mutation<OperationResponse, LinkEnrichedLeadRequest>({
      query: (body) => ({
        url: '/application-administration/enriched-leads/link',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EnrichedLeadsList'],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addFlashMessage('success', data.responseMessage));
        } catch (err) {
          dispatch(addFlashMessage('error', getEnrichedLeadsApiErrorMessage(err, 'Failed to link company. Please try again.')));
        }
      },
    }),

    unlinkEnrichedLead: builder.mutation<OperationResponse, number>({
      query: (enrichedCompanyId) => ({
        url: `/application-administration/enriched-leads/${enrichedCompanyId}/unlink`,
        method: 'POST',
      }),
      invalidatesTags: ['EnrichedLeadsList'],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addFlashMessage('success', data.responseMessage));
        } catch (err) {
          dispatch(addFlashMessage('error', getEnrichedLeadsApiErrorMessage(err, 'Failed to unlink company. Please try again.')));
        }
      },
    }),

    syncEnrichedLeadData: builder.mutation<OperationResponse, { enrichedCompanyId: number; request: SyncEnrichedLeadRequest }>({
      query: ({ enrichedCompanyId, request }) => ({
        url: `/application-administration/enriched-leads/${enrichedCompanyId}/sync`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['EnrichedLeadsList'],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addFlashMessage('success', data.responseMessage));
        } catch (err) {
          dispatch(addFlashMessage('error', getEnrichedLeadsApiErrorMessage(err, 'Failed to sync data. Please try again.')));
        }
      },
    }),

    getAllCorporations: builder.query<{ id: number; name: string }[], void>({
      query: () => ({
        url: '/Corporation/all',
      }),
    }),

  }),
});

export const {
  useGetEnrichedLeadsQuery,
  useLinkEnrichedLeadMutation,
  useUnlinkEnrichedLeadMutation,
  useSyncEnrichedLeadDataMutation,
  useGetAllCorporationsQuery,
} = enrichedLeadsAPI;
